import { cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"


import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { ButtonStyles } from "@/components/Button/ButtonStyles.ts"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/DropdownMenu/DropdownMenu.tsx"
import { MultilineEllipsis } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { CopyRegular, DownloadRegular, Icon16, TerminalRegular } from "@/icons/Icons.ts"
import { copyToClipboard } from "@/lib/std.ts"
import { isPreviewPortBeingListened } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { versionTitle } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import LogoOutline from "./logo-outline.svg?react"

import type { TID, UPreviewProcessesApiState } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskPreviewModel } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import type { Property } from "csstype"


function getPreviewStatus(previewState: UPreviewProcessesApiState | null) {
  if (previewState == null) {
    return null
  }
  switch (previewState.className) {
    case "Starting":
      return previewState.status
    case "Failed":
      return previewState.message
    case "Stopped":
      return "Preview was stopped"
    case "Running":
      if (!isPreviewPortBeingListened(previewState)) {
        return "Preview port is not being listened"
      }
      return null // todo: which branch is preview on
  }
}

type PreviewWidgetContentProps = {
  model: TaskPreviewModel
  width?: Property.Width
  displayStatusBadge?: boolean
  showLoadingAnimation?: boolean
  onOpenLog?: ((logId: string) => void)
}

export const PreviewWidgetContent = observer((
  {
    model,
    width,
    displayStatusBadge,
    showLoadingAnimation,
    onOpenLog
  }: PreviewWidgetContentProps
) => {
  const devMode = useDevMode()

  const previewState = model.previewApiStateWithHistory.get()
  let previewBadge: {
    kind: "error" | "warning" | "success" | "info"
    title: string
  }

  switch (previewState?.className) {
    case undefined:
      previewBadge = { kind: "info", title: "Fetching" }
      break
    case "Starting":
      previewBadge = { kind: "info", title: "Starting" }
      break
    case "Failed":
      previewBadge = { kind: "error", title: "Failed to start" }
      break
    case "Stopped":
      previewBadge = { kind: "warning", title: "Stopped" }
      break
    case "Running": {
      let title = "Running"
      const n = previewState.n
      switch (n) {
        case undefined:
          break
        case 0:
          title += " on the default branch"
          break
        default:
          title += ` on ${versionTitle(n).toLowerCase()}`
          break
      }

      previewBadge = { kind: "success", title }
      break
    }
  }

  const status = getPreviewStatus(previewState)

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  useEffect(() => {
    // process failure is being reported on running env (because logs) using
    if (onOpenLog === undefined || previewState?.className !== "Running") {
      return
    }

    // `null` means it was executed on warmup
    // todo: remove `null` check once we can obtain warmup run logs
    if (previewState.warmupScriptState?.className == "Finished" && previewState.warmupScriptState.exitCode != 0) {
      onOpenLog("before") // todo: get the ID from the backend
      return
    }

    for (const { first: command, second: runState } of previewState.processStates) {
      if (runState.className == "Finished") {
        onOpenLog(command.id)
        return
      }
    }
  }, [previewState, onOpenLog])

  return (
    <PreviewWidgetContentContainer width={width}>
      {(displayStatusBadge === undefined || displayStatusBadge) && (
        <Horizontal alignItems="center" gap="8px">
          <BadgeLabel>Preview status</BadgeLabel>
          <Spring />
          <Badge kind={previewBadge.kind}>{previewBadge.title}</Badge>
        </Horizontal>
      )}

      {previewState?.className == "Stopped" &&
        (
          <EnvironmentWasStopped>
            <LogoOutline />
            <StatusTextBlock>
              <Heading2>
                Preview was stopped
              </Heading2>
              <SecondaryTextSpan>
                To conserve resources, the live preview is automatically shut down after a period of inactivity
              </SecondaryTextSpan>
            </StatusTextBlock>
          </EnvironmentWasStopped>
        )
      }

      {/* Don't render status text for Stopped state - it has a custom renderer above (EnvironmentWasStopped) */}
      {previewState?.className !== "Starting" && previewState?.className !== "Stopped" && status && (
        <Status title={status} lines={4}>{status}</Status>
      )}

      {previewState !== null && (
        <ButtonsContainer showLoadingAnimation={showLoadingAnimation ?? false}>
          <PreviewActionButtons previewState={previewState} model={model} />
        </ButtonsContainer>
      )}

      <ButtonsContainer showLoadingAnimation={showLoadingAnimation ?? false}>
        {devMode && previewState?.className === "Running" && (
          <>
            <Button
              kind="alternative"
              size="small"
              onClick={() => model.restart()}
            >
              Restart preview
            </Button>
            <ConfirmDialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
              trigger={(
                <Button
                  kind="danger"
                  size="small"
                  onClick={() => setConfirmDialogOpen(true)}
                >
                  Recreate preview
                </Button>
              )}
              description="Are you sure you want to recreate the preview? This operation might take several minutes to complete."
              confirmText="Recreate"
              onConfirm={() => {
                void model.recreate()
                setConfirmDialogOpen(false)
              }}
            />
          </>
        )}
      </ButtonsContainer>
    </PreviewWidgetContentContainer>
  )
})

type PreviewActionButtonProps = {
  previewState: UPreviewProcessesApiState
  model: TaskPreviewModel
}

const PreviewActionButtons = observer(({ previewState, model }: PreviewActionButtonProps) => {
  const devMode = useDevMode()

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  if (previewState.className !== "Running") {
    return (
      <>
        {previewState.className !== "Starting" && <StartButton start={model.restart} />}
        {devMode && model.canSshToEnvironment.get() && <ConnectButton model={model} />}
        {devMode && <DownloadRuntimeLogsButton model={model} />}
        {devMode && previewState.className != "Starting" && (
          <ConfirmDialog
            open={confirmDialogOpen}
            onOpenChange={setConfirmDialogOpen}
            trigger={(
              <Button
                kind="danger"
                onClick={() => setConfirmDialogOpen(true)}
              >
                Recreate preview
              </Button>
            )}
            description="Are you sure you want to recreate the preview? This operation might take several minutes to complete."
            confirmText="Recreate"
            onConfirm={() => {
              void model.recreate()
              setConfirmDialogOpen(false)
            }}
          />
        )}
      </>
    )
  } else {
    return (
      <>
        <ConnectButton model={model} />
        <TunnelButton model={model} />
        {devMode && <DownloadRuntimeLogsButton model={model} />}
      </>
    )
  }
})

type RestartButtonProps = {
  start: () => Promise<void>
}

const StartButton = ({ start }: RestartButtonProps) => {
  return (
    <StyledStartButton
      kind="alternative"
      onClick={() => start()}
    >
      Start preview
    </StyledStartButton>
  )
}

export const ConnectButton = (props: {
  model: TaskPreviewModel,
  large?: boolean,
  withIcon?: boolean,
  className?: string
}) => {
  return (
    <DropdownMenu onOpenChange={props.model.onConnectButtonPress}>
      <DropdownMenuTrigger
        className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative, props.large ? ButtonStyles.Size.Large : null, props.className)}
      >
        <Horizontal alignItems="center" gap="4px">
          {props.withIcon && <TerminalRegular className={Icon16} />}
          Connect via SSH
        </Horizontal>
      </DropdownMenuTrigger>
      <ConnectDropdown model={props.model} />
    </DropdownMenu>
  )
}

export const ConnectDropdown = observer((props: { model: TaskPreviewModel }) => {
  return (
    <DropdownMenuContent align="end">
      Use the command below to set up an SSH connection to the preview environment:

      {props.model.connectCommand.get()
        ? (
          <Vertical alignItems="flex-start" gap="4px">
            <SshCommand>{props.model.connectCommand.get()}</SshCommand>
            <CopyToClipboard text={props.model.connectCommand.get()} />
          </Vertical>
        )
        : <Spinner text={ellipsize("Loading")} />}
    </DropdownMenuContent>
  )
})

const TunnelButton = (props: { model: TaskPreviewModel }) => {
  return (
    <DropdownMenu onOpenChange={props.model.onTunnelButtonPress}>
      <DropdownMenuTrigger className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative)}>
        Set up SSH tunnel
      </DropdownMenuTrigger>
      <TunnelDropdown model={props.model} />
    </DropdownMenu>
  )
}

const TunnelDropdown = observer((props: { model: TaskPreviewModel }) => {
  return (
    <DropdownMenuContent align="end">
      Use the command below to create an SSH tunnel to the preview environment:

      {props.model.tunnelCommand.get()
        ? (
          <Vertical alignItems="flex-start" gap="4px">
            <SshCommand>{props.model.tunnelCommand.get()}</SshCommand>
            <CopyToClipboard text={props.model.tunnelCommand.get()} />
          </Vertical>
        )
        : <Spinner text={ellipsize("Loading")} />}
    </DropdownMenuContent>
  )
})

export const DownloadRuntimeLogsButton = observer(({ model, withIcon, className }: {
  model: TaskPreviewModel,
  withIcon?: boolean,
  className?: string
}) => {
  const context = useOrgContext()
  const task = model.task.get()

  if (!task) return null

  return (
    <button
      className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative, className)}
      onClick={() => context.queries.apiClient.downloadPreviewEnvLogs(task.id as TID)}
    >
      <Horizontal alignItems="center" gap="4px">
        {withIcon && <DownloadRegular />}
        Download CodeCanvas logs
      </Horizontal>
    </button>
  )
})

export const CopyToClipboard = (props: { text: string | null }) => {
  return (
    <Button kind="alternative" onClick={() => copyToClipboard(props.text)}>
      <Horizontal alignItems="center" gap="4px">
        <CopyRegular className={Icon16} />Copy
      </Horizontal>
    </Button>
  )
}

const PreviewWidgetContentContainer = styled(Vertical)<{
  width?: Property.Width
}>`
  gap: 24px;

  width: ${props => props.width ? props.width : "auto"};
`

const BadgeLabel = styled.span`
  white-space: nowrap;
`

const ButtonsContainer = styled(Horizontal)<{
  showLoadingAnimation: boolean
}>`
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;

  margin-top: ${props => props.showLoadingAnimation ? "160px" : "0"};

  &:empty {
    display: none;
  }
`

const SshCommand = styled.pre`
  ${textStyles.pre};
`

const Status = styled(MultilineEllipsis)`
  color: var(--secondary-text-color);
  text-align: center;

  view-transition-name: preview-status;
`

const EnvironmentWasStopped = styled(Vertical)`
  align-items: center;
  gap: 24px;
  text-align: center;
  view-transition-name: preview-status;
`

const StatusTextBlock = styled(Vertical)`
  align-items: center;
  gap: 12px;
`

const StyledStartButton = styled(Button)`
  width: auto;
`
